import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';

import { Dashboard } from '@src/main/modules/common/dashboard/domain/Dashboard';
import { StatisticsRepository } from '@src/main/modules/common/statistics/domain/StatisticsRepository';
import { User } from '@src/main/modules/common/user/domain/User';
import { useUserUseCases } from '@src/main/ui/providers/user/hooks/useUserUseCases';

import { UserRepository } from '../../../modules/common/user/domain/UserRepository';

interface ContextState {
  user: User | undefined;
  users: User[] | undefined;
  toggleFavorite: (dashboard: Dashboard) => void;
  addRecentlyOpened: (dashboard: Dashboard) => void;
  addAdminRole: (userId: string, userToAdmin: User) => void;
}

const UserContext = createContext<ContextState>({} as ContextState);
interface Props {
  userRepository: UserRepository;
  statisticsRepository: StatisticsRepository;
}

export const UserContextProvider: FC<PropsWithChildren<Props>> = ({
  children,
  userRepository,
  statisticsRepository,
}) => {
  const { user, users, toggleFavorite, addRecentlyOpened, addAdminRole } = useUserUseCases(
    userRepository,
    statisticsRepository,
  );

  const values = useMemo(
    () => ({
      user,
      users,
      toggleFavorite,
      addRecentlyOpened,
      addAdminRole,
    }),
    [user, users, toggleFavorite, addRecentlyOpened, addAdminRole],
  );

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
};

export const useUserContext = (): ContextState => useContext(UserContext);
